/**
 * Общие детали полей: подпись с «?» и «(необязательно)», подсказка и ошибка
 * под полем. Живут ВНУТРИ полей React Aria (`Select`, `DatePicker`,
 * `TextField` …), потому что подпись, подсказка и ошибка связываются с полем
 * по контексту — отдельным элементом рядом связь не восстановить.
 */
import type { ReactNode } from 'react';
import { FieldError, Label, Text } from 'react-aria-components';
import { useT } from '../../../i18n/hooks';
import { Explained } from '../../../components/InfoPopover';
import { FM } from './messages';
import type { FieldSize } from './types';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** Корневой класс поля: `ac-field` (вид гостя/хокима) + расширение. */
export function fieldClass(size: FieldSize | undefined, className: string | undefined, ...extra: Array<string | false | undefined>): string {
  return cx('ac-field', 'fm-field', size === 'sm' && 'fm-sm', ...extra, className);
}

/** Строковое имя поля для диктора (у кнопки «?» и окна календаря). */
export function labelText(label: ReactNode, ariaLabel: string | undefined, fallback: string): string {
  return typeof label === 'string' ? label : (ariaLabel ?? fallback);
}

export function FieldLabel({ label, ariaLabel, explain, isRequired }: { label: ReactNode; ariaLabel?: string; explain?: ReactNode; isRequired?: boolean }) {
  const t = useT();
  if (!label) return null;
  return (
    <Label>
      <Explained label={labelText(label, ariaLabel, t(FM.explainFallback))} explain={explain}>
        {label}
      </Explained>
      {isRequired === false ? <span className="fm-opt">{t(FM.optional)}</span> : null}
    </Label>
  );
}

export function FieldFoot({ description, errorMessage, isInvalid }: { description?: ReactNode; errorMessage?: ReactNode; isInvalid?: boolean }) {
  return (
    <>
      {description ? (
        <Text slot="description" className="fm-hint">
          {description}
        </Text>
      ) : null}
      {isInvalid && errorMessage ? <FieldError className="ac-error">{errorMessage}</FieldError> : null}
    </>
  );
}
